import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Layers } from 'lucide-react';
import { Card, toast, ConfirmationModal } from '../../components/v2/V2Components';
import RoomService from '../../services/RoomService';

const FLOOR_TYPE_OPTIONS = [
    { value: 'carpet', label: 'Carpet' },
    { value: 'wood', label: 'Wood' },
    { value: 'lvp', label: 'LVP' },
    { value: 'peel_and_stick', label: 'Peel & Stick' },
    { value: 'other', label: 'Other' },
];

const EMPTY_FORM = {
    name: '',
    floor_type: '',
    brand: '',
    color_style: '',
    install_date: '',
    purchase_date: '',
};

/**
 * Hydrate form state from a server floor-detail object. Dates are ISO strings;
 * split at 'T' so the native date input gets a bare YYYY-MM-DD value.
 */
const floorToFormState = (detail) => ({
    name: detail.name || '',
    floor_type: detail.floor_type || '',
    brand: detail.brand || '',
    color_style: detail.color_style || '',
    install_date: detail.install_date ? detail.install_date.split('T')[0] : '',
    purchase_date: detail.purchase_date ? detail.purchase_date.split('T')[0] : '',
});

/**
 * Build a server-ready payload from form state. Empty strings → null so the API
 * doesn't store empty strings that compare differently from missing data.
 */
const buildPayload = (formData) => {
    const cleaned = { ...formData };
    Object.keys(cleaned).forEach(key => {
        if (cleaned[key] === '') cleaned[key] = null;
    });
    cleaned.name = formData.name.trim();
    return cleaned;
};

/**
 * Dedicated create/edit page for a room's floor detail. Replaces the old
 * FloorDetailModal.
 *
 * Routes (registered in App.jsx):
 *   /room/:roomId/floor-details/new           → create mode
 *   /room/:roomId/floor-details/:floorId/edit → edit mode
 *
 * There is no single-record GET endpoint for floor details, so edit mode loads
 * the room's floor-detail list and finds the record by id. When the user
 * arrived from the Floor tab's edit button we pass the record via router state
 * (location.state.floorDetail) as a fast path that skips the fetch.
 */
const FloorDetailForm = () => {
    const { roomId, floorId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEditMode = !!floorId;

    const backToFloorTab = `/room/${roomId}?tab=floor`;

    const [formData, setFormData] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [pendingDiscardDest, setPendingDiscardDest] = useState(null);

    // Snapshot used for dirty-checking. Updated after fetch (edit) so reverting
    // a field back to its loaded value clears the dirty flag.
    const pristineRef = useRef(JSON.stringify(EMPTY_FORM));

    // Generation guard so a superseded fetch can't hydrate the current route.
    // If floorId/roomId change (edit → edit on the same mounted instance)
    // before an in-flight load resolves, the stale response must not commit —
    // otherwise the old floor's data would overwrite the new route and then be
    // saved against the new ids. Mirrors the request-guard pattern RoomDetails
    // uses for its plan fetches.
    const fetchGenerationRef = useRef(0);

    const isDirty = JSON.stringify(formData) !== pristineRef.current;

    // Mirror the dirty flag into a ref so the beforeunload listener can read the
    // latest value without the effect re-subscribing on every keystroke.
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    // --- Load existing floor detail in edit mode ---
    const fetchFloorDetail = useCallback(async () => {
        // Claim this generation; any earlier in-flight call is now superseded.
        const generation = ++fetchGenerationRef.current;
        if (!isEditMode) return;

        // Fast path: the Floor tab's edit button passes the record via state.
        // Synchronous, so there's no window for it to be superseded.
        const preloaded = location.state?.floorDetail;
        if (preloaded && String(preloaded.id) === String(floorId)) {
            const next = floorToFormState(preloaded);
            setFormData(next);
            pristineRef.current = JSON.stringify(next);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const details = await RoomService.getRoomFloorDetails(roomId);
            if (generation !== fetchGenerationRef.current) return; // superseded
            const match = (details || []).find(d => String(d.id) === String(floorId));
            if (!match) {
                toast.error('Floor detail not found');
                navigate(backToFloorTab);
                return;
            }
            const next = floorToFormState(match);
            setFormData(next);
            pristineRef.current = JSON.stringify(next);
        } catch (err) {
            if (generation !== fetchGenerationRef.current) return; // superseded
            toast.error(err.message || 'Failed to load floor detail');
            navigate(backToFloorTab);
        } finally {
            if (generation === fetchGenerationRef.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, roomId, floorId, navigate]);

    useEffect(() => {
        fetchFloorDetail();
    }, [fetchFloorDetail]);

    // Browser-level guard against reload/close while dirty. Subscribes once and
    // reads the current dirty state via the ref (see isDirtyRef above). Note
    // this only covers tab close/reload — in-app navigation is guarded by the
    // Cancel button's discard modal (handleNavigation), matching PaintForm.
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirtyRef.current) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // --- Save ---
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Name is required');
            return;
        }

        setSaving(true);
        try {
            const payload = buildPayload(formData);
            if (isEditMode) {
                await RoomService.updateRoomFloorDetail(roomId, floorId, payload);
                toast.success('Floor detail updated');
            } else {
                await RoomService.createRoomFloorDetail(roomId, payload);
                toast.success('Floor detail added');
            }
            // Clear dirty so the discard guard doesn't fire on the way out.
            pristineRef.current = JSON.stringify(formData);
            navigate(backToFloorTab);
        } catch (err) {
            toast.error(err.message || 'Failed to save floor detail');
        } finally {
            setSaving(false);
        }
    };

    // --- Cancel / back nav with dirty-check ---
    const handleNavigation = (destination = backToFloorTab) => {
        if (isDirty) {
            setPendingDiscardDest(destination);
        } else {
            navigate(destination);
        }
    };

    const handleConfirmDiscard = () => {
        const dest = pendingDiscardDest;
        setPendingDiscardDest(null);
        // Bypass the dirty check on this navigation by clearing pristine.
        pristineRef.current = JSON.stringify(formData);
        navigate(dest ?? backToFloorTab);
    };

    const labelClass = 'block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1 theme-transition';
    const inputClass = 'w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none theme-transition';

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            {/* Page header */}
            <div className="min-w-0">
                <h1 className="text-2xl font-bold text-[var(--text-main)] flex items-center gap-2 theme-transition">
                    <Layers size={24} />
                    {isEditMode ? 'Edit Floor Detail' : 'Add Floor Detail'}
                </h1>
                <p className="text-sm text-[var(--text-secondary)] theme-transition">
                    Track flooring materials for easy replacement and refinishing.
                </p>
            </div>

            <Card className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="floor-name" className={labelClass}>
                            Name <span className="text-[var(--error)]">*</span>
                        </label>
                        <input
                            id="floor-name"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="e.g. Main Floor, Entry Tile"
                            className={inputClass}
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="floor-type" className={labelClass}>Floor Type</label>
                            <select
                                id="floor-type"
                                value={formData.floor_type}
                                onChange={(e) => handleChange('floor_type', e.target.value)}
                                className={inputClass}
                            >
                                <option value="">Select type...</option>
                                {FLOOR_TYPE_OPTIONS.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="floor-brand" className={labelClass}>Brand</label>
                            <input
                                id="floor-brand"
                                value={formData.brand}
                                onChange={(e) => handleChange('brand', e.target.value)}
                                placeholder="e.g. Shaw, Mohawk"
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="floor-color-style" className={labelClass}>Color / Style</label>
                        <input
                            id="floor-color-style"
                            value={formData.color_style}
                            onChange={(e) => handleChange('color_style', e.target.value)}
                            placeholder="e.g. Weathered Oak"
                            className={inputClass}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="floor-install-date" className={labelClass}>Install Date</label>
                            <input
                                id="floor-install-date"
                                type="date"
                                value={formData.install_date}
                                onChange={(e) => handleChange('install_date', e.target.value)}
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label htmlFor="floor-purchase-date" className={labelClass}>Purchase Date</label>
                            <input
                                id="floor-purchase-date"
                                type="date"
                                value={formData.purchase_date}
                                onChange={(e) => handleChange('purchase_date', e.target.value)}
                                className={inputClass}
                            />
                        </div>
                    </div>

                    {/* Action bar */}
                    <div className="flex space-x-3 pt-4 border-t border-[var(--border)]">
                        <button
                            type="button"
                            onClick={() => handleNavigation(backToFloorTab)}
                            className="flex-1 py-3 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition theme-transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 py-3 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Add')}
                        </button>
                    </div>
                </form>
            </Card>

            <ConfirmationModal
                isOpen={pendingDiscardDest !== null}
                onClose={() => setPendingDiscardDest(null)}
                onConfirm={handleConfirmDiscard}
                title="Discard unsaved changes?"
                message="Your changes won't be saved if you leave now."
                confirmText="Discard"
                isDanger
            />
        </div>
    );
};

export default FloorDetailForm;
